import React, { memo } from 'react'

import { useMediaQueries } from 'hooks'

import AttentionModalCheckbox from 'components/_old/AttentionModalCheckbox/AttentionModalCheckbox.jsx'
import Button from 'components/_old/Button/Button.jsx'
import { Typo } from 'components/_old/Typo/Typo'

import { Paper } from 'components/atoms/Paper'
import { Typography } from 'components/atoms/Typography'

import { AttentionModalText, type PresetAlike } from 'components/molecules/AttentionModalText'
import StickedIfNotDesktop from 'components/molecules/StickedIfNotDesktop/StickedIfNotDesktop.jsx'

import AttentionModalBase from 'components/organisms/AttentionModal/AttentionModal.jsx'

type AttentionModalProps = {
  visible: boolean
  presets: PresetAlike[]
  desiredIndex: number
  recommendedIndex: number
  portfolioChanged: boolean
  onSubmit: () => Promise<unknown>
  onClose: (submitDisabled: boolean) => void
}

const AttentionModal = memo<AttentionModalProps>(
  ({ visible = false, presets, desiredIndex, recommendedIndex, portfolioChanged = false, onSubmit, onClose }) => {
    const { desktop } = useMediaQueries()

    const checkboxText = (
      <Typography size={14}>
        <Typo>I understand the risk associated with this portfolio and am prepared to continue on this basis.</Typo>
      </Typography>
    )

    const renderSubmit = (disabled = false): React.ReactElement => (
      <StickedIfNotDesktop into="insideModal">
        <Button
          onClick={onSubmit}
          mods={{
            size: 'big block',
            theme: desktop ? null : 'not-rounded',
          }}
          disabled={disabled}
          data-test-id="attentionModalSubmit"
        >
          Confirm
        </Button>
      </StickedIfNotDesktop>
    )

    return (
      <AttentionModalCheckbox
        recommendedIndex={recommendedIndex}
        desiredIndex={desiredIndex}
        checkboxText={checkboxText}
      >
        {({ shouldShowCheckbox = false, checkbox, submitDisabled }) => (
          <AttentionModalBase
            visible={visible}
            closeAttentionModal={() => {
              onClose(submitDisabled)
            }}
            submit={renderSubmit(submitDisabled)}
          >
            <Typography size={14}>
              <AttentionModalText
                presets={presets}
                desiredPresetIndex={desiredIndex}
                recommendedPresetIndex={recommendedIndex}
                postText={
                  portfolioChanged
                    ? `\n\nIf you continue, the change request will be processed within 2 business days`
                    : null
                }
              />
            </Typography>
            {shouldShowCheckbox ? <Paper top={24}>{checkbox}</Paper> : null}
          </AttentionModalBase>
        )}
      </AttentionModalCheckbox>
    )
  },
  (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps),
)
AttentionModal.displayName = 'AttentionModal'

export { AttentionModal }
